// Destination Country Step

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Image
} from "react-native";
import { loadCountries } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import SearchIcon from "../../../../../../../../assets/images/searchIcon.png";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import RadioSelectedIcon from "../../../../../../../../assets/images/Radio-Select-Button.png"


type ApiCountry = {
  id?: number;
  CountryCode: string;
  CountryName: string;
  IsActive: number | boolean;
};

type ChooseCountryStepProps = {
  onCountrySelected?: (code: string) => void;
};

export default function ChooseCompanyStep({ onCountrySelected }: ChooseCountryStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [countries, setCountries] = useState<ApiCountry[]>([]);
  const setSelectedDestinationCountryCode = useSessionStore((s: SessionState) => s.setSelectedDestinationCountryCode);
  const setSelectedDestinationCountryName = useSessionStore((s: SessionState) => s.setSelectedDestinationCountryName);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await loadCountries();
        const apiCountries: ApiCountry[] = (result?.data ?? []).filter((c: ApiCountry) => {
          const isActive = typeof c.IsActive === 'number' ? c.IsActive === 1 : !!c.IsActive;
          return isActive && !!c.CountryName && !!c.CountryCode;
        });
        setCountries(apiCountries);
      } catch (e) {
        console.error('Failed to load countries in ChooseCountryStep', e);
      }
    };
    fetchCountries();
  }, []);

  // Filter countries by search
  const filteredCountries = useMemo(() => {
    const q = search.toLowerCase();
    return countries.filter((c) => (c.CountryName || '').toLowerCase().includes(q));
  }, [countries, search]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("SEARCH_FOR_COMPANY_TO_MAKE_PAYMENT")}</Text>

      {/* Search bar */}
      <View style={styles.searchContainer}>
        <Image
          source={SearchIcon}
          style={{height: 24, width: 24, marginRight: 8}}
          resizeMode='contain'
        />
        <TextInput
          style={styles.searchInput}
          placeholder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
          value={search}
          onChangeText={setSearch}
        />
      </View>
      <Text style={styles.recentText}>
        {t("RECENT")}
      </Text>

      {/* Country list */}
      <FlatList
        data={filteredCountries}
        keyExtractor={(item) => item.CountryCode}
        style={{ flex: 1, width: "100%" }} 
        renderItem={({ item }) => (
          <TouchableOpacity
            style={styles.companyItemContainer}
            onPress={() => {
              setSelectedCountry(item.CountryName);
              setSelectedDestinationCountryName(item.CountryName);
              setSelectedDestinationCountryCode(item.CountryCode);
              onCountrySelected?.(item.CountryCode);
            }}
          >
            <Image
              source={EnglishFlag}
              style={styles.companyLogo}
            />
            <Text style={styles.companyName}>{item.CountryName}</Text>
          </TouchableOpacity>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontSize: 28,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 34,
    marginBottom: 36,
    color: Colors.titleTextColor,
  },
  searchContainer: {
    flexDirection: 'row',
    backgroundColor: Colors.searchBackground,
    padding: 10,
    borderRadius: 8,
    marginBottom: 36,
    alignItems: 'center'
  },
  searchInput: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    padding: 10,
    lineHeight: 24,
    color: Colors.subTitleColor,
  },
  recentText: {
    fontSize: 20,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 26,
    color: Colors.titleTextColor,
  },
  companyItemContainer: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    paddingRight: 16,
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: 50,
    marginRight: 16
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  companyName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 24,
    color: Colors.black,
  }
});
